/*
 * Visualiseur InstrumenPoche en Javascript et SVG
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const kernel = require('../global/global')
const Vect = require('../types/vecteur')
const Compas = require('../instruments/compas')
const Equerre = require('../instruments/equerre')
const Rapporteur = require('../instruments/rapporteur')
const Regle = require('../instruments/regle')
const Requerre = require('../instruments/requerre')
const Crayon = require('../instruments/crayon')
const ActionCoucherCompas = require('../actions/actionCoucherCompas')
const ActionCreation = require('../actions/actionCreation')
const ActionEcarterCompas = require('../actions/actionEcarterCompas')
const ActionEcrireTexte = require('../actions/actionEcrireTexte')
const ActionGlisser = require('../actions/actionGlisser')
const ActionLeverCompas = require('../actions/actionLeverCompas')
const ActionMasquer = require('../actions/actionMasquer')
const ActionMasquerInstrument = require('../actions/actionMasquerInstrument')
const ActionModifierLongueur = require('../actions/actionModifierLongueur')
const ActionMontrer = require('../actions/actionMontrer')
const ActionMontrerGraduations = require('../actions/actionMontrerGraduations')
const ActionMontrerInstrument = require('../actions/actionMontrerInstrument')
const ActionMontrerNombres = require('../actions/actionMontrerNombres')
const ActionNommerPoint = require('../actions/actionNommerPoint')
const ActionPause = require('../actions/actionPause')
const ActionRotationInstrument = require('../actions/actionRotationInstrument')
const ActionRetourner = require('../actions/actionRetourner')
const ActionRotationObjet = require('../actions/actionRotationObjet')
const ActionTranslationInstrument = require('../actions/actionTranslationInstrument')
const ActionTranslationObjet = require('../actions/actionTranslationObjet')
const ActionZoomInstrument = require('../actions/actionZoomInstrument')
const ActionZoomObjet = require('../actions/actionZoomObjet')
const ActionModifierRayon = require('../actions/actionModifierRayon')
const Angle = require('../objets/angle')
const AngleDroit = require('../objets/angleDroit')
const Arc = require('../objets/arc')
const Gabarit = require('../objets/gabarit')
const Axe = require('../objets/axe')
const DemiDroite = require('../objets/demiDroite')
const Droite = require('../objets/droite')
const Imag = require('../objets/Imag')
const LigneContinue = require('../objets/ligneContinue')
const MarqueSegment = require('../objets/marqueSegment')
const NomPoint = require('../objets/nomPoint')
const Point = require('../objets/point')
const Polygone = require('../objets/polygone')
const Quadrillage = require('../objets/quadrillage')
const Repere = require('../objets/repere')
const Segment = require('../objets/segment')
const Texte = require('../objets/texte')

module.exports = IepDoc

// la liste des outils
const outils = ['compas', 'equerre', 'rapporteur', 'regle', 'requerre', 'crayon']

/**
 * Document représentant une figure affichée dans le SVG d'id idDoc
 * @constructor
 * @param {Element} svg  Le SVG contenant la figure
 * @param {string} chdoc La chaîne contenant le code XML décrivant la figure.
 * @param {boolean} [autoStart=false] : Si true l'animation démarre automatiquement
 * @param {Object} [options]
 * @param {boolean} [options.debug] passer true pour avoir des infos de debug en console
 * @param {boolean} [options.autoStart=false] passer true pour démarrer l'animation dès le chargement
 */
function IepDoc (svg, chdoc, options) {
  /*
   * L'élément html du svg
   * @type Element
   */
  this.svg = svg
  // pour la compatibilité ascendante, on traite le 3e argument booléen comme autostart
  if (typeof options === 'boolean') {
    this.autoStart = options
    options = {}
  } else {
    if (typeof options !== 'object') options = {}
    this.autoStart = Boolean(options.autoStart)
  }
  this.debug = Boolean(options.debug)
  /**
   * Pointera sur la fenêtre contenant le code xml
   * @type Window
   */
  this.windowxml = null
  let ch = chdoc
  // On ajoute l'en-tête xml si elle a été ommise.
  if (ch.search(/<?xml/gi) === -1) ch = '<?xml version="1.0" encoding="UTF-8"?>' + ch
  // On élimine les <action_ suivis de chiffres
  ch = ch.replace(/<action[_]\d*/gi, '<action')
  // On élimine les remarques html
  ch = ch.replace(/<!--.*-->/g, '')
  // On corrige les oublis d'espaces et les -- dans les attributs
  ch = ch.replace(/(="[^"]*")([^ ])/g, '$1 $2').replace(/--/g, '')
  // On corrige les éventuels << ou >>
  ch = ch.replace(/<</g, '<').replace(/>>/g, '>')
  // Certains auteurs ont mis des <actiontempo= sans espace ou autre
  ch = ch.replace(/<action(\w)/gi, '<action $1')
  // Les commentaires peuvent un attribut texte ce qui pose des problèmes dans elimineDoublonsXML
  // On les remplace par un attribut texteCommentaire
  ch = ch.replace(/<commentaire\s*texte\s*=/gi, '<commentaire texteCommentaire =')
  // On corrige les attributs en doublon
  ch = kernel.elimineDoublonsXML(ch)
  // On élimine l'en-tête qui contient parfois des caractères bizarres
  /**
   * La string xml du doc
   * @type string
   */
  this.codexml = ch // Mémorisé pour pouvoir être affiche quand on clique sur le bouton XML

  // on corrige le tag ouvrant et fermant, parfois folklorique dans les scripts existants
  const ind1 = ch.search(/<\s*INSTRUMENPOCHE/gi)
  const ind2 = ch.indexOf('>', ind1)
  const ind3 = ch.search(new RegExp('<\\s*/INSTRUMENPOCHE', 'gi')) // Pas de > dans la recherche car certains auteurs se sont trompés sur la balise de fin
  this.chdoc = '<INSTRUMENPOCHE>' + ch.substring(ind2 + 1, ind3) + '</INSTRUMENPOCHE>'

  this.actions = [] // Un tableau qui contiendra toutes les actions de la figure.
  // Une liste des éléments graphiques de type objet graphique traités par la figure.
  // Chaque élément sera identifié par un n° d'id et par son type
  // Lorsqu'un élément de même id et de même nature qu'un élément déjà existant est créé, lors de l'exécution
  // de l'action de création, son élément graphique remplacera celui de l'élement déjà existant
  // dans le DOM du svg de la figure
  this.elements = []
  this.compasRetourne = false
  // On crée les instruments dès le début car à chaque fois qu'un instrument est translaté, tourné, zoomé,
  // certains objet créés ont besoin de connaître l'état des instruments pour leur création
  this.compasLeve = null
  this.compas = new Compas(this)
  this.equerre = new Equerre(this)
  this.rapporteur = new Rapporteur(this)
  this.regle = new Regle(this)
  this.requerre = new Requerre(this)
  this.crayon = new Crayon(this)
  // On parse le xml
  this.xmldoc = kernel.parseXMLDoc(this.chdoc)
  this.tabact = this.xmldoc.getElementsByTagName('action')
  this.started = false
  this.animationEnCours = true // Sera mis à false pour le pas à pas
  // indiceActionEnCours est l'indice de la première future action qui devra être exécutée
  this.indiceActionEnCours = 0
  this.creeActions()
  const t = this
  // Les événements souris pour pouvoir gérer les déplacements de la barre d'icônes
  svg.addEventListener('mousemove', function (ev) { t.onmousemove(ev, ev.clientX, ev.clientY) }, false)
  svg.addEventListener('mouseup', function () { t.onmouseup() }, false)
  svg.addEventListener('mouseleave', function () { t.onmouseleave() }, false)
  svg.addEventListener('touchmove', function (ev) { t.onmousemove(ev, ev.touches[0].pageX, ev.touches[0].pageY) }, false)
  svg.addEventListener('touchend', function () { t.onmouseup() }, false)
  svg.addEventListener('touchcancel', function () { t.onmouseup() }, false)
  svg.addEventListener('touchleave', function () { t.onmouseleave() }, false)
  if (this.debug) console.log('iepDoc instancié :', this)
};

/**
 * Fontion apppelée à la fin du chargement de certains onjets dont le chargement est asynchrone : les images
 * et les textes dont l'écriture nécessite l'utilisation du LaTeX.
 * Renvoie true si tous les objets de la figure sont bien chargés.
 * Au bout de 10 secondes écoulées affiche un message d'erreur
 * @returns
 */
IepDoc.prototype.waitForReadyState = function () {
  if (this.started) return
  const ready = this.verifyReadyState()
  if (ready) {
    this.start()
  } else {
    const self = this
    setTimeout(function () {
      self.waitForReadyState()
    }, 200) // On vérifie toutes les 200ms
  }
}

/**
 * Fonction renvoyant true seulement si tous les objets de la figure sont bien chargés
 * @returns {boolean}
 */
IepDoc.prototype.verifyReadyState = function () {
  if (this.debug) {
    const pasPretes = this.actions.filter(action => !action.ready)
    if (pasPretes.length) console.log(`${pasPretes.length} actions pas encore prêtes (sur ${this.actions.length})`)
    else console.log(`Les ${this.actions.length} actions sont prêtes`)
  }
  return this.actions.every(action => action.isReady)
}

/**
 * Fonction créant tous les éléments graphiques des aactions de création de la figure.
 * Seules les actions de créations d'objets ont une fonction creegElement faisant quelquechose
 */
IepDoc.prototype.creeElementsGraphiques = function () {
  this.actions.forEach(action => action.creegElement())
}

/**
 * Fonction réinitilisant la position initiale de tous les instruments et les cachant.
 */
IepDoc.prototype.initialiseOutils = function () {
  outils.forEach(outil => {
    this[outil].initialisePosition()
    this[outil].montre(false)
  })
  this.compas.leve = false
}

/**
 * Fonction retirnat du DOM du SVG les éléments graphiques des outils et les recréant
 * de façon à ce que les outils soient créées dans le DOM après les éléments graphiques
 * des objets de la figure.
 */
IepDoc.prototype.recreeOutils = function () {
  outils.forEach(outil => {
    this[outil].updateg()
    this[outil].montre(false)
  })
  this.compasLeve = null
}

/**
 * Fonction lancée après le chargement complet de la figure.
 * Toutes les actions de création d'objet de la figure créent leur propre élément graphique dans le DOM du svg
 * qu'elles stockent dans une variable this.g. Ces éléments sont initialeùent tous cachés.
 * Ensuite on recrée dans le DOM les éléments graphiques des outils pour qu'ils soient situés après les objets créés.
 * Puis on crée les icônes et on les initialise à leur état initial  pour le démarrage de la figure.
 */
IepDoc.prototype.start = function () {
  this.started = true
  this.creeElementsGraphiques()
  this.recreeOutils()
  this.creeIcones()
  this.InitialiseIcones()
  if (this.autoStart) {
    this.onPlay()
  } else {
    this.montreIcone('Pause', false)
    this.montreIcone('Play', true)
  }
}

/**
 * Fonction créant les icônes de la figure.
 */
IepDoc.prototype.creeIcones = function () {
  // Toutes les images seront contenues dans un g element appelé barreIcones
  const nbIcones = 8
  const t = this
  this.barreIcones = document.createElementNS(kernel.svgsn, 'g')
  this.creeElementsPourIcones()
  this.iconGoBegin = this.iconeGoBegin(0, 0, 'fonce')
  this.iconGoBeginGris = this.iconeGoBegin(0, 0, 'clair')
  this.iconGoBegin.onclick = function () { t.onGoBegin() }
  this.barreIcones.appendChild(this.iconGoBegin)
  this.barreIcones.appendChild(this.iconGoBeginGris)
  this.iconStepPrev = this.iconeStepPrev(32, 0, 'fonce')
  this.iconStepPrevGris = this.iconeStepPrev(32, 0, 'clair')
  this.iconStepPrev.onclick = function () { t.onStepPrev() }
  this.barreIcones.appendChild(this.iconStepPrev)
  this.barreIcones.appendChild(this.iconStepPrevGris)
  this.iconPause = this.iconePause(64, 0, 'fonce')
  this.iconPauseGris = this.iconePause(64, 0, 'clair')
  this.iconPause.onclick = function () { t.onPause() }
  this.barreIcones.appendChild(this.iconPause)
  this.barreIcones.appendChild(this.iconPauseGris)
  this.iconPlay = this.iconePlay(96, 0, 'fonce')
  this.iconPlayGris = this.iconePlay(96, 0, 'clair')
  this.iconPlay.onclick = function () { t.onPlay() }
  this.barreIcones.appendChild(this.iconPlay)
  // Icône rouge à activer quand on rencontre une pause
  // Cette icône ne sera visible et active que quand on rencontre une action de pause
  this.iconPlay2 = this.iconePlay(96, 0, 'rouge')
  this.iconPlay2.setAttribute('visibility', 'hidden')
  this.iconPlay2.onclick = function () { t.onPlayContinuer() }
  this.barreIcones.appendChild(this.iconPlay2)
  this.barreIcones.appendChild(this.iconPlayGris)
  this.iconRestart = this.iconeRestart(128, 0, 'fonce')
  this.iconRestartGris = this.iconeRestart(128, 0, 'clair')
  this.iconRestart.onclick = function () { t.onRestart() }
  this.barreIcones.appendChild(this.iconRestart)
  this.barreIcones.appendChild(this.iconRestartGris)
  this.iconStepNext = this.iconeStepNext(160, 0, 'fonce')
  this.iconStepNextGris = this.iconeStepNext(160, 0, 'clair')
  this.iconStepNext.onclick = function () { t.onStepNext() }
  this.barreIcones.appendChild(this.iconStepNext)
  this.barreIcones.appendChild(this.iconStepNextGris)
  this.iconGoEnd = this.iconeGoEnd(192, 0, 'fonce')
  this.iconGoEndGris = this.iconeGoEnd(192, 0, 'clair')
  this.iconGoEnd.onclick = function () { t.onGoEnd() }
  this.barreIcones.appendChild(this.iconGoEnd)
  this.barreIcones.appendChild(this.iconGoEndGris)
  this.iconXML = this.iconeXML(224, 0, 'fonce')
  this.iconXMLGris = this.iconeXML(224, 0, 'clair')
  this.iconXML.onclick = function () { t.onXML() }
  this.barreIcones.appendChild(this.iconXML)
  this.barreIcones.appendChild(this.iconXMLGris)

  this.hauteurBarre = 32
  this.demiLargeurBarre = nbIcones * 16
  // this.xbarre et this.ybarre contiennent les coordonnées initiales de la barre d'outils dans le SVG
  this.xbarre = 50 // Barre placée à gauche de bord
  this.ybarre = 4
  this.barreIcones.setAttribute('transform', 'translate(' + this.xbarre + ',4)')
  this.svg.appendChild(this.barreIcones)
  // Ajout ge gestionnaires de souris sur la barre d'icônes
  this.isDraggingBarre = false
  this.barreIcones.addEventListener('mousedown', function (ev) { t.onmousedownbarre(ev.clientX, ev.clientY) }, false)
  this.barreIcones.addEventListener('mousemove', function (ev) { t.onmousemovebarre(ev.clientX, ev.clientY) }, false)
  // C'est le SVG qui gère les événements mouseup
  this.barreIcones.addEventListener('touchstart', function (ev) { t.onmousedownbarre(ev.touches[0].pageX, ev.touches[0].pageY) }, false)
  this.barreIcones.addEventListener('touchmove', function () { t.onmousemovebarre() }, false)
  // C'est le svg qui gère les événements touhcance et touchend
  // this.barreIcones.addEventListener("touchcancel",function() {t.onmouseupbarre()},false);
  // Initialisation de la position relative de la barre d'outils
  // this.deltax et this.deltay contiendront de décalage relatif de la barre d'outils par rapport
  // à sa position initiale lorsqu'on la fait glisser.
  this.deltax = 0
  this.deltay = 0
}

/**
 * Fonction appelée quand on clique ou fait un appui tactile sur la barre d'icônes de la figure
 * @param {Integer} xmouse : l'abscisse du point cliqué par la souris
 * @param {Integer} ymouse : l'ordonnée du point cliqué par la souris
 */
IepDoc.prototype.onmousedownbarre = function (xmouse, ymouse) {
  if (this.animationEnCours) return
  this.isDraggingBarre = true
  this.xcapturebarre = xmouse
  this.ycapturebarre = ymouse
}

/**
 * Fonction appelée au survol de la barre d'icônes par la souris ou du pointeur tactive
 */
IepDoc.prototype.onmousemovebarre = function () {
  if (this.animationEnCours) {
    this.barreIcones.style.cursor = 'default'
    this.isDraggingBarre = false
    return
  }
  this.barreIcones.style.cursor = 'pointer'
}

/**
 * Fonction appelée au ssurvol du svg par la souris ou le pointeur tactile
 * @param {event} ev
 * @param {Integer} xmouse : l'abscisse pointée par la souris
 * @param {Integer} ymouse : l'ordonnée pointée par la souris
 */
IepDoc.prototype.onmousemove = function (ev, xmouse, ymouse) {
  if (this.isDraggingBarre) {
    const w = parseInt(this.svg.getAttribute('width'))
    const h = parseInt(this.svg.getAttribute('height'))
    const dx = xmouse - this.xcapturebarre
    const dy = ymouse - this.ycapturebarre
    const deltax = this.deltax + dx
    const deltay = this.deltay + dy
    const xpos = this.xbarre + deltax
    const ypos = this.ybarre + deltay
    if ((xpos <= w - 2 * this.demiLargeurBarre) && (xpos >= 0) &&
            (ypos >= 0) && (ypos <= h - this.hauteurBarre)) {
      this.barreIcones.setAttribute('transform', 'translate(' + xpos + ',' + ypos + ')')
      this.deltax = deltax
      this.deltay = deltay
      this.xcapturebarre = xmouse
      this.ycapturebarre = ymouse
    }
    ev.preventDefault() // Pour éviter que des éléments texte soient sélectionnés en bougeant la souris
  }
  // ev.preventDefault(); // Pour éviter que des éléments texte soient sélectionnés en bougeant la souris
  // Abandonné car alors on e peut pls zoomer ou dézoomer sur périphérique mobile
}
/**
 * Fonction appelée lorsque le bouton de la souris est relâché sur le svg
 */
IepDoc.prototype.onmouseup = function () {
  this.isDraggingBarre = false
}
/**
 * Fonction appelée quand on relâche le bouton de la souris sur la figure
 */
IepDoc.prototype.onmouseleave = function () {
  this.isDraggingBarre = false
}
/**
 * Fonction appelée quand on clique sur le bouton XML de la barre d'outils
 * Ouvre une fenêtre affichant le code XML de la figure
 */
IepDoc.prototype.onXML = function () {
  if (this.windowxml == null) this.windowxml = this.popup()
  else this.windowxml.focus()
}

/**
 * Fonction activant ou désactivant l'icône de nom nomIcone
 * Si bvisible est true, l'icône normale est montrée et l'icône grisée est cachée
 * Si bvisible est false,l'icône normale est masquée et l'icône grisée est montrée
 * @param {string} nomIcone
 * @param {boolean} bvisible
 */
IepDoc.prototype.montreIcone = function (nomIcone, bvisible) {
  const nom = 'icon' + nomIcone
  this[nom].setAttribute('visibility', bvisible ? 'visible' : 'hidden')
  this[nom + 'Gris'].setAttribute('visibility', bvisible ? 'hidden' : 'visible')
}
/**
 * Fonction activant l'icône rouge Continuer et désactivant les autres icônes
 */
IepDoc.prototype.activeIconeContinuer = function () {
  this.montreIcone('GoBegin', false)
  this.montreIcone('StepPrev', false)
  this.montreIcone('Pause', false)
  this.montreIcone('Play', false)
  this.montreIcone('Restart', false)
  this.montreIcone('StepNext', false)
  this.montreIcone('GoEnd', false)
  this.iconPlayGris.setAttribute('visibility', 'hidden')
  this.iconPlay2.setAttribute('visibility', 'visible')
  this.montreIcone('XML', true)
}
/**
 * Fonction désactivant toutes les icônes suf l'icône de pause
 */
IepDoc.prototype.InitialiseIcones = function () {
  this.montreIcone('GoBegin', false)
  this.montreIcone('StepPrev', false)
  this.montreIcone('Pause', true)
  this.montreIcone('Play', false)
  this.montreIcone('Restart', false)
  this.montreIcone('StepNext', false)
  this.montreIcone('GoEnd', false)
  this.montreIcone('XML', false)
}
/**
 * Fonction appelée lors d'un clic sur l'icône de retour de la figure au début
 */
IepDoc.prototype.onGoBegin = function () {
  this.animationEnCours = false
  this.initialise()
  this.montreIcone('GoBegin', false)
  this.montreIcone('StepPrev', false)
  this.montreIcone('Pause', false)
  this.montreIcone('Play', true)
  this.montreIcone('Restart', false)
  this.montreIcone('StepNext', true)
  this.montreIcone('GoEnd', true)
  this.montreIcone('XML', true)
  this.indiceActionEnCours = 0
}
/**
 * Fonction appelée lors d'un clic sur l'icône de saut à la fin de la figure
 */
IepDoc.prototype.onGoEnd = function () {
  this.animationEnCours = false
  this.initialise()
  this.montreIcone('GoBegin', true)
  this.montreIcone('StepPrev', true)
  this.montreIcone('Pause', false)
  this.montreIcone('Play', false)
  this.montreIcone('Restart', true)
  this.montreIcone('StepNext', false)
  this.montreIcone('GoEnd', false)
  this.montreIcone('XML', true)
  this.executeJusque(this.actions.length - 1)
  this.indiceActionEnCours = this.actions.length
}
/**
 * Fonction appelée quand on clique sur l'icône play de la figure
 */
IepDoc.prototype.onPlay = function () {
  this.animationEnCours = true
  this.montreIcone('GoBegin', false)
  this.montreIcone('StepPrev', false)
  this.montreIcone('Pause', true)
  this.montreIcone('Play', false)
  this.montreIcone('Restart', false)
  this.montreIcone('StepNext', false)
  this.montreIcone('GoEnd', false)
  this.montreIcone('XML', false)
  this.actions[this.indiceActionEnCours].execute(false)
}
/**
 * Fonction appelée quand on clique sur l'icône continuer de la figure
 */

IepDoc.prototype.onPlayContinuer = function () {
  if (this.indiceActionEnCours === this.actions.length) {
    this.onGoEnd()
    return
  }
  this.iconPlay2.setAttribute('visibility', 'hidden')
  this.montreIcone('GoBegin', false)
  this.montreIcone('StepPrev', false)
  this.montreIcone('Pause', true)
  this.montreIcone('Play', false)
  this.montreIcone('Restart', false)
  this.montreIcone('StepNext', false)
  this.montreIcone('GoEnd', false)
  this.montreIcone('XML', false)
  this.animationEnCours = true
  this.indiceActionEnCours++
  this.actions[this.indiceActionEnCours].execute(false)
}
/**
 * Fonction appelée quand on clique sur l'icône de retour au début et jouer
 */

IepDoc.prototype.onRestart = function () {
  const t = this
  if (this.animationEnCours) {
    this.animationEnCours = false
    setTimeout(function () {
      t.onRestart()
    }, 100)
    return
  }
  if (this.animationEnCours) return
  this.animationEnCours = false
  this.initialise()
  this.indiceActionEnCours = 0
  this.onPlay()
}
/**
 * Fonction appelée quand on clique sur l'icône pause de la figure
 */

IepDoc.prototype.onPause = function () {
  const t = this
  t.animationEnCours = false
  // On laisse 5 dixièmes de seconde pour finir l'animation en cours.
  setTimeout(function () {
    t.montreIcone('GoBegin', true)
    t.montreIcone('StepPrev', true)
    t.montreIcone('Pause', false)
    t.montreIcone('Play', true)
    t.montreIcone('Restart', true)
    t.montreIcone('StepNext', true)
    t.montreIcone('GoEnd', true)
    t.montreIcone('XML', true)
  }, 500)
}
/**
 * Fonction appelée quand on clique sur l'icône suivant de la figure (pas à pas)
 */
IepDoc.prototype.onStepNext = function () {
  const nbActions = this.actions.length
  this.animationEnCours = false
  if (this.indiceActionEnCours < nbActions) {
    if (this.actions[this.indiceActionEnCours].actionVisible()) this.indiceActionEnCours++
    else this.indiceActionEnCours = this.indiceProchaineActionVisible(this.indiceActionEnCours) + 1
    this.executeJusque(this.indiceActionEnCours - 1)
  }
  const derniereAction = this.indiceActionEnCours === this.actions.length
  this.montreIcone('GoBegin', true)
  this.montreIcone('StepPrev', true)
  this.montreIcone('Pause', false)
  this.montreIcone('Play', !derniereAction)
  this.montreIcone('Restart', true)
  this.montreIcone('StepNext', !derniereAction)
  this.montreIcone('GoEnd', !derniereAction)
  this.montreIcone('XML', true)
}
/**
 * Fonction appelée quand on clique sur l'icône précédent de la figure (pas à pas)
 */
IepDoc.prototype.onStepPrev = function () {
  this.animationEnCours = false
  // this.indiceActionEnCours--;
  this.indiceActionEnCours = this.indicePrecedenteActionVisible(this.indiceActionEnCours - 1)
  if (this.indiceActionEnCours > 0) this.executeJusque(this.indiceActionEnCours - 1)
  const premiereAction = this.indiceActionEnCours === 0
  this.montreIcone('GoBegin', !premiereAction)
  this.montreIcone('StepPrev', !premiereAction)
  this.montreIcone('Pause', false)
  this.montreIcone('Play', true)
  this.montreIcone('Restart', false)
  this.montreIcone('StepNext', true)
  this.montreIcone('GoEnd', true)
  this.montreIcone('XML', true)
}

/**
 * Fonction cachant tous les éléments graphiques de la figure, réinitialisant tous les outils
 * et réinitialisant la position de tous les objets ou instruments qui peuvent être translatés, tournés ou zoomés
 * à savoir les points et images
 */
IepDoc.prototype.initialise = function () {
  let i, el
  const svg = this.svg
  for (i = 0; i < svg.childNodes.length; i++) {
    el = svg.childNodes[i]
    if (el !== this.barreIcones) {
      try { // Pour compatibilité avec explorer
        svg.childNodes[i].setAttribute('visibility', 'hidden')
      } catch (e) {}
    }
  }
  for (i = 0; i < this.actions.length; i++) {
    const action = this.actions[i]
    if (action instanceof ActionCreation) action.objet.initialisePosition()
  }
  this.initialiseOutils()
}
/**
 * Fonction donnant l'état de visibilité de l'instrument instrument après l'exécution
 * de l'action d'indice indaction
 * @param {iep.instrumentAncetre} instrument
 * @param {Integer} indaction
 * @returns {boolean}
 */
IepDoc.prototype.getInstrumentVisibility = function (instrument, indaction) {
  if (indaction === -1) return false
  let action
  let visible = false
  for (let i = 0; i <= indaction; i++) {
    action = this.actions[i]
    if (action instanceof ActionMontrerInstrument) {
      if (action.instrument === instrument) visible = true
    } else if (action instanceof ActionMasquerInstrument) {
      if (action.instrument === instrument) visible = false
    }
  }
  return visible
}
/**
 * Fonction donnant l'état de visibilité de l'objet objet après l'exécution
 * de l'action d'indice indaction
 * @param {iep.ObjetBase} objet
 * @param {Integer} indaction
 * @returns {boolean}
 */
IepDoc.prototype.getObjectVisibility = function (object, indaction) {
  if (indaction === -1) return false
  let action
  let visible = false
  for (let i = 0; i <= indaction; i++) {
    action = this.actions[i]
    if (action instanceof ActionMontrer) {
      if (action.objet === object) visible = true
    } else if (action instanceof ActionMasquer) {
      if (action.objet === object) visible = false
    }
  }
  return visible
}
/**
 * Fonction donnant l'état du compas (levé ou couché) après l'exécution
 * de l'action d'indice indaction.
 * Si le compas est couché, renvoie "couche", s'il est leve, renvoie "leve"
 * @param {Integer} indaction
 * @returns {string}
 */
IepDoc.prototype.getCompasStatus = function (indaction) {
  if (indaction === -1) return 'couche'
  let couche = true
  let action
  for (let i = 0; i <= indaction; i++) {
    action = this.actions[i]
    if (action instanceof ActionCoucherCompas) {
      couche = true
    } else if (action instanceof ActionLeverCompas) {
      couche = false
    }
  }
  return couche ? 'couche' : 'leve'
}
/**
 * Fonction renvoyant l'indice de la prochaine action qui aura une action visible sur la figure
 * à partir de l'indice ind
 * Si aucune action visible ne figure après ind, renvoie this.actions.length
 * @param {type} ind
 * @returns {iep.iepDoc.prototype.indiceProchaineActionVisible.i|iep.iepDoc.actions.length}
 */
IepDoc.prototype.indiceProchaineActionVisible = function (ind) {
  if (ind >= this.actions.length) return this.actions.length - 1
  for (let i = ind; i < this.actions.length; i++) {
    if (this.actions[i].actionVisible()) return i
  }
  return this.actions.length - 1
}
/**
 * Fonction renvoyant l'indice de la précédene action qui aura une action visible sur la figure
 * à partir de l'indice ind
 * Si aucune action visible ne figure avant ind, renvoie 0
 * @param {type} ind
 * @returns {iep.iepDoc.prototype.indiceProchaineActionVisible.i|iep.iepDoc.actions.length}
 */

IepDoc.prototype.indicePrecedenteActionVisible = function (ind) {
  if (ind < 0) return 0
  if (ind === this.actions.length) ind--
  for (let i = ind; i >= 0; i--) {
    if (this.actions[i].actionVisible()) return i
  }
  return 0
}
/**
 * Fonction montrant les instruments à leur étape de visibilité à l'étape n
 * @param {Integer} n : l'indice de la dernière action exécutée
 */
IepDoc.prototype.montreInstrumentsEtape = function (n) {
  let visible = this.getInstrumentVisibility(this.compas, n)
  if (this.compas.leve) this.compasLeve.montre(visible)
  else this.compas.montre(visible)
  visible = this.getInstrumentVisibility(this.crayon, n)
  this.crayon.montre(visible)
  visible = this.getInstrumentVisibility(this.equerre, n)
  this.equerre.montre(visible)
  visible = this.getInstrumentVisibility(this.rapporteur, n)
  this.rapporteur.montre(visible)
  visible = this.getInstrumentVisibility(this.regle, n)
  this.regle.montre(visible)
  visible = this.getInstrumentVisibility(this.requerre, n)
  this.requerre.montre(visible)
}
/**
 * Fonction excécutant sans animation toutes les actions de la figure jusqu'à l'indice indfin compris
 * @param {Integer} indfin
 */
IepDoc.prototype.executeJusque = function (indfin) {
  if (indfin >= this.actions.length) return
  this.initialise()
  this.initialiseOutils()
  this.animationEnCours = false
  for (let i = 0; i <= indfin; i++) { this.actions[i].execute(true) } // true pour que l'exécution soit immédiate
  this.montreInstrumentsEtape(indfin)
}
/**
 * Fonction recherchant s'il y a déjà dans la liste doc.elements un élément de même id et de même
 * nature.
 * Si oui on remplace l'ancien. Sinon on rajoute element à la liste.
 * @param {iep.doc.elements[]} element
 */
IepDoc.prototype.addElement = function (element) {
  let i, el
  for (i = 0; i < this.elements.length; i++) {
    el = this.elements[i]
    // S'il existe un élément de même type et de même id, on le remplace par le nouveau
    if ((el.id === element.id) && (el.objet === element.objet)) {
      this.elements[i] = element
      return
    }
  }
  // S'il n'y a pas d'élément du même type et de même id, on ajoute le nouvel élément
  this.elements.push(element)
}
/**
 * Fonction appelée dans le cas où on est sur que la liste doc.elements ne contient pas
 * déjà un élément de même id et de même nature que element
 * @param {type} element
 */
IepDoc.prototype.pushElement = function (element) {
  this.elements.push(element)
}
/**
 * Fonction renvoyant l'objet de elements d'id égale à id s'il y en a une et sinon null
 * @param {type} id
 * @returns {Boolean|iep.iepDoc.elements|iep.iepDoc.prototype.element.el}
 * Attention : Le quadrillage est le seul objet a ne pas avoir d'id et il ne peut y en avoir qu'un par figure
 */
IepDoc.prototype.getElement = function (id, nature) {
  let i, el
  if (nature === 'quadrillage') {
    for (i = 0; i < this.elements.length; i++) {
      el = this.elements[i]
      if (el.objet === nature) return el
    }
  } else {
    for (i = 0; i < this.elements.length; i++) {
      el = this.elements[i]
      if ((el.id === id) && (el.objet === nature)) return el
    }
  }
  return null
}
/**
 * Fonction remplaçant l'élément de elements did id par e s'il y en a un
 * S'il n'y a pas délément d'id id renvoie false
 * @param {type} id
 * @param {type} e
 * @returns {boolean}
 */
IepDoc.prototype.setElement = function (id, e) {
  for (let i = 0; i < this.elements.length; i++) {
    if (this.elements[i].id === id) {
      this.elements[i] = e
      return true
    }
  }
  return false
}
/**
 * Fonction ajoutant l'action action à la liste d'actions listeActions
 * @param {type} action
 */
IepDoc.prototype.ajouteAction = function (action) {
  // On donne à action un indice por qu'lle sache quelle est son rand dans la liste
  // Ainsi elle pourra ensuite indiquer si son action sur la figure est visible ou non
  action.indice = this.actions.length
  this.actions.push(action)
}
/**
 * Fonction créant toutes les actions de la figure
 * @param {type} ind
 */
IepDoc.prototype.creeActions = function () {
  let ind, el, at, ato, objet, action, forme, xcrayon, ycrayon, id,
    angle, sens, vitesse, abs, ord, cibles, coord, tempo, len, ecart, v1, echelle, point, cible
  for (ind = 0; ind < this.tabact.length; ind++) {
    el = this.tabact[ind]
    id = this.getId(el)
    at = el.getAttribute('mouvement')
    tempo = el.getAttribute('tempo')
    try {
      let abscisse_bas_droite, ecartAngle // faut éviter de déclarer dans un case directement, on le fait dans le bloc parent (eslint no-case-declarations)
      switch (at) {
        case 'creer':
          ato = el.getAttribute('objet')
          // Si on a déjà créé un objet avec le même id c'est une erreur et on saute a création
          if (this.getElement(id, ato) !== null) break

          if (ato != null) {
            switch (ato) {
              case 'point':
                objet = new Point(this, id, parseFloat(this.getAbs(el)), parseFloat(this.getOrd(el)),
                  this.getCouleur(el), this.getEpaisseur(el))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'angle':
                objet = new Angle(this, id, this.getAbs(el), this.getOrd(el), el.getAttribute('rayon'),
                  el.getAttribute('angle1'), el.getAttribute('angle2'), this.getCouleur(el), this.getEpaisseur(el),
                  this.getOpaciteFond(el), el.getAttribute('forme'))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'angle_droit':
                objet = new AngleDroit(this, id, el.getAttribute('abscisse_sommet'), el.getAttribute('ordonnee_sommet'),
                  el.getAttribute('abscisse_inter'), el.getAttribute('ordonnee_inter'), this.getCouleur(el), this.getEpaisseur(el))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'longueur':
                objet = new MarqueSegment(this, id, this.getAbs(el), this.getOrd(el),
                  this.getCouleur(el), this.getEpaisseur(el),
                  el.getAttribute('forme'))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'texte':
                objet = new Texte(this, id, this.getAbs(el), this.getOrd(el), this.getCouleur(el), el.getAttribute('taille'))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'repere':
                objet = new Repere(this, el.getAttribute('hauteur'), el.getAttribute('largeur'),
                  el.getAttribute('haut'), el.getAttribute('gauche'), el.getAttribute('Xgrad'),
                  el.getAttribute('Ygrad'), el.getAttribute('Xmin'), el.getAttribute('Xmax'),
                  el.getAttribute('Ymin'), el.getAttribute('Ymax'), this.getCouleur(el),
                  el.getAttribute('grille'), el.getAttribute('axes'), el.getAttribute('etiquettes'))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'quadrillage':
                abscisse_bas_droite = el.getAttribute('abscisse_bas_droite')
                if (abscisse_bas_droite != null) { // Syntaxe non documentée dans la doc
                  abscisse_bas_droite = parseFloat(abscisse_bas_droite) / 30
                  const ordonnee_bas_droite = parseFloat(el.getAttribute('ordonnee_bas_droite')) / 30
                  const abscisse_haut_gauche = parseFloat(el.getAttribute('abscisse_haut_gauche')) / 30
                  const ordonnee_haut_gauche = parseFloat(el.getAttribute('ordonnee_haut_gauche')) / 30
                  objet = new Quadrillage(
                    this,
                    el.getAttribute('quadrillage'),
                    String(ordonnee_bas_droite - ordonnee_haut_gauche),
                    String(abscisse_bas_droite - abscisse_haut_gauche),
                    ordonnee_haut_gauche,
                    abscisse_haut_gauche,
                    this.getCouleur(el)
                  )
                  action = new ActionCreation(this, 'quadrillageIEP', objet, tempo)
                } else {
                  objet = new Quadrillage(this, el.getAttribute('quadrillage'), el.getAttribute('hauteur'),
                    el.getAttribute('largeur'), el.getAttribute('haut'), el.getAttribute('gauche'), this.getCouleur(el))
                }
                action = new ActionCreation(this, 'quadrillageIEP', objet, tempo)
                break

              case 'axe':
                objet = new Axe(this, el.getAttribute('pente'), el.getAttribute('largeur'),
                  el.getAttribute('haut'), el.getAttribute('gauche'), el.getAttribute('distanceBord'),
                  el.getAttribute('Xgrad'), el.getAttribute('Xmin'), el.getAttribute('Xmax'), this.getCouleur(el))
                action = new ActionCreation(this, id, objet, tempo)
                break

              case 'gabarit':
                ecartAngle = el.getAttribute('ecart_angle')
                if (ecartAngle === null) ecartAngle = '-30'
                objet = new Gabarit(this, id, el.getAttribute('abscisse'), el.getAttribute('ordonnee'), el.getAttribute('rayon'),
                  ecartAngle, this.getCouleur(el), this.getEpaisseur(el), this.getCouleurFond(el), this.getOpaciteTrait(el))
                action = new ActionCreation(this, id, objet, tempo)
                break

              default:
                action = null // Pour ce que j'aurais oublié d'implémenter
            }

            if (action) {
              this.ajouteAction(action)
              action.setReady()
            }
          }
          break
        case 'montrer':
          ato = el.getAttribute('objet')
          if (kernel.estInstrument(ato)) {
            abs = el.getAttribute('abscisse')
            ord = el.getAttribute('ordonnee')
            action = new ActionMontrerInstrument(this, this[ato], abs, ord, tempo)
            if (((ato === 'crayon') || (ato === 'compas')) && (abs != null) && (ord != null)) this[ato].translate(parseFloat(abs), parseFloat(ord))
          } else action = new ActionMontrer(this, id, ato, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'masquer':
          ato = el.getAttribute('objet')
          if (kernel.estInstrument(ato)) action = new ActionMasquerInstrument(this, this[ato], tempo)
          else action = new ActionMasquer(this, id, ato, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'tracer':
          ato = el.getAttribute('objet')
          cible = el.getAttribute('cible')
          if (cible != null) {
            objet = this.getElement(cible, 'point') // Le point vers lequel on veut translater l'objet
            abs = objet.xcons
            ord = objet.ycons
          } else {
            abs = parseFloat(this.getAbs(el))
            ord = parseFloat(this.getOrd(el))
          }
          switch (ato) {
            case 'crayon':
              forme = el.getAttribute('forme')
              xcrayon = this.crayon.x
              ycrayon = this.crayon.y
              id = this.getId(el)
              switch (forme) {
                case null: // Cas d'un segment
                  if (isNaN(abs) || isNaN(ord)) { // Cas d'une forme libre pas déclarée
                    objet = new LigneContinue(this, id, this.crayon.x, this.crayon.y, el.getAttribute('abscisses'),
                      el.getAttribute('ordonnees'), this.getCouleur(el), this.getEpaisseur(el), this.getOpaciteTrait(el),
                      this.getStyleTrait(el), this.getStyle(el))
                    len = objet.abs.length
                    this.crayon.translate(objet.abs[len - 1], objet.ord[len - 1])
                  } else {
                    objet = new Segment(this, id, xcrayon, ycrayon, abs, ord, this.getCouleur(el),
                      this.getEpaisseur(el), this.getOpaciteTrait(el), this.getStyleTrait(el), this.getStyle(el))
                    this.crayon.translate(abs, ord)
                  }
                  break
                case 'droite': // Cas d'une droite
                  if (isNaN(abs) || isNaN(ord)) break
                  objet = new Droite(this, id, xcrayon, ycrayon, abs, ord, this.getCouleur(el),
                    this.getEpaisseur(el), this.getOpaciteTrait(el), this.getStyleTrait(el), this.getStyle(el))
                  this.crayon.translate(abs, ord)
                  break
                case 'demidroite': // Cas d'une demi-droite
                  if (isNaN(abs) || isNaN(ord)) break
                  objet = new DemiDroite(this, id, xcrayon, ycrayon, abs, ord, this.getCouleur(el),
                    this.getEpaisseur(el), this.getOpaciteTrait(el), this.getStyleTrait(el), this.getStyle(el))
                  this.crayon.translate(abs, ord)
                  break
                case 'polygone':
                  cibles = el.getAttribute('cibles')
                  if (cibles === null) {
                    objet = new Polygone(this, id, el.getAttribute('abscisses'),
                      el.getAttribute('ordonnees'), this.getCouleur(el), this.getEpaisseur(el),
                      this.getCouleurFond(el), el.getAttribute('opacite'))
                  } else {
                    coord = this.getCoord(cibles)
                    objet = new Polygone(this, id, coord.abs, coord.ord,
                      this.getCouleur(el), this.getEpaisseur(el),
                      this.getCouleurFond(el), el.getAttribute('opacite'))
                  }
                  len = objet.abs.length
                  this.crayon.translate(objet.abs[len - 1], objet.ord[len - 1])
                  break
                case 'libre':
                  objet = new LigneContinue(this, id, this.crayon.x, this.crayon.y, el.getAttribute('abscisses'),
                    el.getAttribute('ordonnees'), this.getCouleur(el), this.getEpaisseur(el), this.getOpaciteTrait(el),
                    this.getStyleTrait(el), this.getStyle(el))
                  len = objet.abs.length
                  this.crayon.translate(objet.abs[len - 1], objet.ord[len - 1])
              }
              vitesse = el.getAttribute('vitesse')
              if (vitesse == null) vitesse = '4'
              action = new ActionCreation(this, this.getId(el), objet, tempo, vitesse)
              this.ajouteAction(action)
              action.setReady()
              break
            case 'trait':
              vitesse = el.getAttribute('vitesse')
              if (vitesse === null) vitesse = 10000 // Instantané si vitesse pas précisé
              objet = new Segment(this, id, el.getAttribute('abscisse1'), el.getAttribute('ordonnee1'),
                el.getAttribute('abscisse2'), el.getAttribute('ordonnee2'), this.getCouleur(el),
                this.getEpaisseur(el), this.getOpaciteTrait(el), this.getStyleTrait(el), this.getStyle(el))
              action = new ActionCreation(this, this.getId(el), objet, tempo, vitesse) // Pas d'animation pour les traits
              this.ajouteAction(action)
              action.setReady()
              break
            case 'compas':
              objet = new Arc(this, id, this.compas.x, this.compas.y, this.compas.ecart,
                this.getAngle(el, 'debut'), this.getAngle(el, 'fin'), this.getCouleur(el), this.getEpaisseur(el), this.getOpaciteTrait(el),
                this.getStyleTrait(el))
              vitesse = el.getAttribute('sens')
              if (vitesse == null) vitesse = '8'
              action = new ActionCreation(this, this.getId(el), objet, tempo, vitesse)
              this.ajouteAction(action)
              action.setReady()
              break
            case 'angle_droit': // Certains scripts utilisent tracer au lieu de créer pour les angles droits ...
              objet = new AngleDroit(this, id, el.getAttribute('abscisse_sommet'), el.getAttribute('ordonnee_sommet'),
                el.getAttribute('abscisse_inter'), el.getAttribute('ordonnee_inter'), this.getCouleur(el), this.getEpaisseur(el))
              action = new ActionCreation(this, this.getId(el), objet, tempo, vitesse)
              this.ajouteAction(action)
              action.setReady()
              break
          }
          break
        case 'rotation':
          ato = el.getAttribute('objet')
          sens = el.getAttribute('sens')
          if (kernel.estInstrument(ato)) {
            cible = el.getAttribute('cible')
            // Au cas ou un auteur a mis à la fois un attribut cible et un attribut angle,
            // C'est l'attribut angle qui semble l'emporter (constaté par des tests).
            if ((cible != null) && (el.getAttribute('angle') === null)) {
              objet = this.getElement(cible, 'point') // Le point vers lequel on veut translater l'objet
              abs = objet.xcons
              ord = objet.ycons
              v1 = new Vect(this[ato].x, this[ato].y, abs, ord)
              if (v1.presqueNul()) angle = 0
              else angle = -v1.angle()
            } else {
              if (ato === 'compas') angle = this.getAngle(el, 'angle')
              else angle = el.getAttribute('angle')
            }
            action = new ActionRotationInstrument(this, this[ato], angle, tempo, sens)
          } else {
            // On vérifie que l'action est cohérente (que l'objet cible existe)
            if (this.getElement(id, ato) === null) action = null
            else {
              angle = el.getAttribute('angle')
              action = new ActionRotationObjet(this, id, angle, ato, tempo, sens)
            }
          }
          if (action !== null) {
            this.ajouteAction(action)
            action.setReady()
          }
          break
        case 'translation':
          ato = el.getAttribute('objet')
          cible = el.getAttribute('cible')
          // Au cas où on a spécifié abscisse et ordonnée et cible, cible aa priorité
          if (cible != null) {
            objet = this.getElement(cible, 'point') // Le point vers lequel on veut translater l'objet
            abs = objet.xcons
            ord = objet.ycons
          } else {
            abs = parseFloat(this.getAbs(el))
            ord = parseFloat(this.getOrd(el))
          }
          vitesse = el.getAttribute('vitesse')
          if (kernel.estInstrument(ato)) {
            this[ato].x = abs
            this[ato].y = ord
            action = new ActionTranslationInstrument(this, this[ato], abs, ord, tempo, vitesse)
          } else {
            // On vérifie que l'action est cohérente
            if (this.getElement(id, ato) === null) action = null
            else {
              // Dans le cas où on translate un point il faut modifier ses champs xcons et ycons
              // pour que tout objet ultérieur l'utilisant comme cible soit correctement initialisé
              if (ato === 'point') {
                point = this.getElement(id, 'point')
                point.xcons = abs
                point.ycons = ord
              }
              action = new ActionTranslationObjet(this, id, abs, ord, ato, tempo, vitesse)
            }
          }
          if (action !== null) {
            this.ajouteAction(action)
            if ((ato === 'crayon') || (ato === 'compas')) { this[ato].translate(abs, ord) }
            action.setReady()
          }
          break
        case 'zoom':
          ato = el.getAttribute('objet')
          vitesse = el.getAttribute('vitesse')
          if (kernel.estInstrument(ato)) {
            echelle = el.getAttribute('echelle')
            this[ato].zoomfactor = parseFloat(echelle) / 100
            action = new ActionZoomInstrument(this, this[ato], echelle, tempo, vitesse)
          } else {
            // On ne peut zoomer que sur un texte ou une image
            if ((ato === 'texte' || (ato === 'image'))) action = new ActionZoomObjet(this, id, el.getAttribute('echelle'), ato, tempo, vitesse)
            else action = null
          }
          if (action !== null) {
            this.ajouteAction(action)
            action.setReady()
          }
          break
        case 'ecarter':
          cible = el.getAttribute('cible')
          if (cible != null) {
            objet = this.getElement(cible, 'point') // Le point vers lequel on veut translater l'objet
            v1 = new Vect(this.compas.x, this.compas.y, objet.xcons, objet.ycons)
            ecart = v1.norme()
          } else ecart = parseFloat(el.getAttribute('ecart')) * this.compas.zoomfactor
          this.compas.ecart = ecart
          action = new ActionEcarterCompas(this, ecart, tempo, el.getAttribute('vitesse'))
          this.ajouteAction(action)
          action.setReady()
          break
        case 'lever':
          action = new ActionLeverCompas(this, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'coucher':
          action = new ActionCoucherCompas(this, tempo)
          this.ajouteAction(action)
          action.setReady()
          break

        case 'masquer_nombres':
          action = new ActionMontrerNombres(this, false, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'montrer_nombres':
          action = new ActionMontrerNombres(this, true, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'graduations':
          ato = el.getAttribute('objet')
          action = new ActionMontrerGraduations(this, this[ato], true, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'vide':
          ato = el.getAttribute('objet')
          action = new ActionMontrerGraduations(this, this[ato], false, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'retourner':
          this.compasRetourne = !this.compasRetourne
          action = new ActionRetourner(this, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'glisser':
          action = new ActionGlisser(this, el.getAttribute('abscisse'), tempo, el.getAttribute('vitesse'))
          this.ajouteAction(action)
          action.setReady()
          break
        case 'chargement':
          // Attention, pour l'image, pas d'appel de creeAction(ind+1)
          // car c'est une fonction de callBack qui s'en charge une fois l'image chargée
          objet = new Imag(this, id, el.getAttribute('url'))
          objet.prepareAction()
          // Pas de action.setReady car il faut attendre que la figure soit chargée
          break
        case 'ecrire': // Texte
          action = new ActionEcrireTexte(this, id, this.getCouleur(el), el.getAttribute('taille'),
            el.getAttribute('texte'), el.getAttribute('style'), this.getCouleurFondTexte(el),
            el.getAttribute('opacite_fond'),
            this.getCouleurCadre(el), el.getAttribute('epaisseur_cadre'),
            el.getAttribute('marge'), el.getAttribute('marge_gauche'),
            el.getAttribute('marge_droite'), el.getAttribute('marge_haut'),
            el.getAttribute('marge_bas'), tempo)
          this.ajouteAction(action)
          action.prepare()
          break
        case 'nommer':
          // on regarde si le point à nommer a été créé sinon erreur de script
          if (this.getElement(id, 'point') !== null) {
            // Dans certains script on nommait un point avec une chaîne vide ce qui n'a pas de sens.
            const nomPoint = el.getAttribute('nom')
            if (nomPoint !== '') {
              objet = new NomPoint(this, id, this.getAbs(el), this.getOrd(el),
                nomPoint, this.getCouleur(el))
              action = new ActionNommerPoint(this, objet, tempo)
              this.ajouteAction(action)
              action.prepare()
            }
          }
          break
        case 'pause':
          action = new ActionPause(this, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
        case 'modifier_longueur':
          ato = el.getAttribute('objet')
          action = new ActionModifierLongueur(this, this[ato], el.getAttribute('longueur'), tempo)
          this.ajouteAction(action)
          action.setReady()
          break
          // Modification du rayon d'un gabarit
        case 'modifier_rayon':
          ato = el.getAttribute('objet')
          action = new ActionModifierRayon(this, id, el.getAttribute('rayon'), ato, tempo)
          this.ajouteAction(action)
          action.setReady()
          break
      }
    } catch (e) {
      // On ne fait rien si xml incorrect
    }
  }
  // Une fois les actions créées on attend que les éléments asynchrones soient prêts pour continuer
  // en laissant 10 secondes maxi pour le chargement de la figure
  const self = this
  setTimeout(function () {
    if (!self.started) {
      console.error('10s après l’instanciation IepDoc la figure n’est toujours pas prête')
      window.alert('Le chargement de la figure IEP a échoué')
      self.started = true
    }
  }, 20000) // On laisse maxi 10 secondes à la figure pour se charger

  this.waitForReadyState()
}
/**
 * Si immediat est true, les actions sont exécutées immédiatement, sans animation et sans temporisation
 * @param {type} immediat
 */
IepDoc.prototype.execute = function (immediat) {
  this.indiceActionEnCours = 0
  this.actions[0].execute(immediat)
}
/**
 * Fonction appelée lorsqu'une action a été exécutée pour passer à l'exécution suivante.
 * Si immediat est true, on retourne sans rien faire
 * Si animationECours en false, on revient sans rien faire (pour arrêter une animation en cours)
 * Si l'action.tempo n'est pas nul, on attend action.tempo millièmes de seconde avant de passer à l'action suivante
 * indiceActionEnCours est l'indice de la prochaine action à exécuter
 * @param {boolean} immediat
 */
IepDoc.prototype.actionSuivante = function (immediat) {
  const imm = (arguments.length === 0) ? false : immediat
  if (!this.animationEnCours && !imm) {
    // if (this.indiceActionEnCours != this.actions.length-1) this.indiceActionEnCours++;
    return
  } // Pas de passage à l'action suivante pour le mode pas à pas.
  const action = this.actions[this.indiceActionEnCours]
  if ((action.tempo === null) || imm) this.passageActionSuivante(imm)
  else {
    const t = this
    setTimeout(function () { t.passageActionSuivante(imm) }, action.tempo)
  }
}
/**
 * Fonction appelée par actionSuivante pour passer à l'action suivante
 * indiceActionEnCours est l'indice de la prochaine action à exécuter
 * @param {boolean} immediat
 */
IepDoc.prototype.passageActionSuivante = function (immediat) {
  // IL peut arriver qu'on arrive ici alors qu'on a cliqué pour arrêter une animation, cliqué rapidement
  // pour aller à la fin alors que l'animation n'était pas finie...
  if (this.indiceActionEnCours >= this.actions.length) return
  if (this.indiceActionEnCours !== this.actions.length - 1) {
    this.indiceActionEnCours++
    this.actions[this.indiceActionEnCours].execute(immediat)
  } else {
    this.indiceActionEnCours++
    this.montreIcone('GoBegin', true)
    this.montreIcone('StepPrev', true)
    this.montreIcone('Pause', false)
    this.montreIcone('Play', false)
    this.montreIcone('StepNext', false)
    this.montreIcone('GoEnd', false)
    this.montreIcone('Restart', true)
    this.montreIcone('XML', true)
    this.animationEnCours = false
  }
}
// Attention : Pour les fonctions suivantes ne pas utiliser de test d'identité ===
/**
 * Fonction renvoyant l'attribut couleur d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getCouleur = function (el) {
  const coul = el.getAttribute('couleur')
  if (coul === null) return 'black'
  else return kernel.couleur(coul)
}
IepDoc.prototype.getCouleurCadre = function (el) {
  const coul = el.getAttribute('couleur_cadre')
  if (coul === null) return null
  else return kernel.couleur(coul)
}
/**
 * Fonction renvoyant l'attribut couleur de fond d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */

IepDoc.prototype.getCouleurFond = function (el) {
  const coul = el.getAttribute('couleur_fond')
  if (coul === null) return null
  else return kernel.couleur(coul)
}
/**
 * Fonction renvoyant l'attribut couleur de fond d'une action d'écriture de texte
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getCouleurFondTexte = function (el) {
  const coul = el.getAttribute('couleur_fond')
  if (coul === null) return null
  else return kernel.couleur(coul)
}
/**
 * Fonction renvoyant l'attribut épaisseur d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */

IepDoc.prototype.getEpaisseur = function (el) {
  const ep = el.getAttribute('epaisseur')
  if ((ep === null) || (parseInt(ep) === 0)) return '1'
  else return ep
}
/**
 * Fonction renvoyant l'attribut opacité de trait d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getOpaciteTrait = function (el) {
  const op = el.getAttribute('opacite')
  if (op === null) return '100'
  else return op
}
/**
 * Fonction renvoyant l'attribut opacité de fond d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getOpaciteFond = function (el) {
  const op = el.getAttribute('opacite')
  if (op === null) return '50'
  else return op
}
/**
 * Fonction renvoyant l'attribut le style de trait d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getStyleTrait = function (el) {
  const st = el.getAttribute('pointille')
  if (st == null) return 'continu'; else return st
}
/**
 * Fonction renvoyant l'attribut style de flèce d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */

IepDoc.prototype.getStyle = function (el) {
  const st = el.getAttribute('style')
  if (st == null) return 'normal'; else return st
}
/**
 * Fonction renvoyant l'attribut id d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */

IepDoc.prototype.getId = function (el) {
  return el.getAttribute('id')
}
/**
 * Fonction renvoyant l'attribut abscisse de trait d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getAbs = function (el) {
  return el.getAttribute('abscisse')
}
/**
 * Fonction renvoyant l'attribut ordonnée de trait d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */
IepDoc.prototype.getOrd = function (el) {
  const ord = el.getAttribute('ordonnee')
  if (ord !== null) return ord; else return el.getAttribute('ordonnée')
}
/**
 * Fonction renvoyant l'attribut point d'une action de création de polygône
 * @param {type} cible
 * @returns {iep.iepDoc.prototype.getCoord.res|Object}
 */
IepDoc.prototype.getCoord = function (cible) {
  let abs = ''
  let ord = ''
  cible.split(',').forEach((elt, i) => {
    const sep = (i === 0) ? '' : ','
    const obj = this.getElement(elt, 'point')
    abs += sep + obj.xcons
    ord += sep + obj.ycons
  })
  return {
    abs,
    ord
  }
}
/**
 * Fonction renvoyant l'attribut angle d'une action
 * @param {Element} el element retourné par this.xmldoc.getElementsByTagName("action")[]
 * @returns {string|Couleur}
 */

IepDoc.prototype.getAngle = function (el, attribute) {
  const a = el.getAttribute(attribute)
  if (a == null) return null
  if (this.compasRetourne) return String(parseFloat(a) + 180)
  else return a
}
/**
 * Fonction créant l'icône play de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconePlay.svg}
 */
IepDoc.prototype.iconePlay = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  const pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '-5,8 8,0 -5,-8')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  return svg
}
/**
 * Fonction créant l'icône pause de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 */
IepDoc.prototype.iconePause = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  let rect = document.createElementNS(kernel.svgsn, 'rect')
  rect.setAttribute('x', -6)
  rect.setAttribute('y', -7)
  rect.setAttribute('width', 3)
  rect.setAttribute('height', 14)
  rect.setAttribute('style', 'stroke:white;fill:white;')
  rect.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(rect)
  rect = document.createElementNS(kernel.svgsn, 'rect')
  rect.setAttribute('x', 2)
  rect.setAttribute('y', -7)
  rect.setAttribute('width', 3)
  rect.setAttribute('height', 14)
  rect.setAttribute('style', 'stroke:white;fill:white;')
  rect.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(rect)
  // svg.setAttribute("transform","translate(16,16)");
  return svg
}
/**
 * Fonction créant l'icône stepNext de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconeStepNext.svg}
 */
IepDoc.prototype.iconeStepNext = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  const rect = document.createElementNS(kernel.svgsn, 'rect')
  rect.setAttribute('x', 5)
  rect.setAttribute('y', -6)
  rect.setAttribute('width', 3)
  rect.setAttribute('height', 12)
  rect.setAttribute('style', 'stroke:white;fill:white;')
  rect.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(rect)
  const pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '-7,6 2,0 -7,-6')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  return svg
}
/**
 * Fonction créant l'icône stepPrev de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconeStepPrev.svg}
 */
IepDoc.prototype.iconeStepPrev = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  const rect = document.createElementNS(kernel.svgsn, 'rect')
  rect.setAttribute('x', -7)
  rect.setAttribute('y', -6)
  rect.setAttribute('width', 3)
  rect.setAttribute('height', 12)
  rect.setAttribute('style', 'stroke:white;fill:white;')
  rect.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(rect)
  const pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '7,6 -2,0 7,-6')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  return svg
}
/**
 * Fonction créant l'icône goEnd de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconeGoEnd.svg}
 */
IepDoc.prototype.iconeGoEnd = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  let pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '-6,7 1,0 -6,-7')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '3,7 10,0 3,-7')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  return svg
}
/**
 * Fonction créant l'icône goBegin de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconeGoBegin.svg}
 */
IepDoc.prototype.iconeGoBegin = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  let pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '6,7 -1,0 6,-7')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  pol = document.createElementNS(kernel.svgsn, 'polygon')
  pol.setAttribute('points', '-3,7 -10,0 -3,-7')
  pol.setAttribute('style', 'stroke:white;fill:white;')
  pol.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(pol)
  return svg
}
/**
 * Fonction créant l'icône restart de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconeRestart.svg}
 */
IepDoc.prototype.iconeRestart = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  const path = document.createElementNS(kernel.svgsn, 'path')
  path.setAttribute('d', 'M 0 -8 A 8 8 -90 1 1 -8 0')
  path.setAttribute('style', 'stroke:white;stroke-width:3;fill:none;')
  path.setAttribute('marker-end', 'url(#markerarrow')
  path.setAttribute('transform', 'translate(16,16)')
  svg.appendChild(path)
  return svg
}
/**
 * Fonction créant l'icône XML de la figure
 * Si teinte == "fonce", la couleur bleu foncée est employée
 * Si "teinte == "clair", la couleur gris clair est employée
 * @param {Integer} x : l'abscisse de l'icône relative à la barre d'icônes
 * @param {Integer} y : l'ordonnée de l'icône relative à la barre d'icônes
 * @param {string} teinte : "fonce" ou "clair"
 * @returns {iep.iconeXML.svg}
 */
IepDoc.prototype.iconeXML = function (x, y, teinte) {
  const svg = document.createElementNS(kernel.svgsn, 'svg')
  svg.setAttribute('x', x)
  svg.setAttribute('y', y)
  svg.setAttribute('width', 32)
  svg.setAttribute('height', 32)
  const circ = document.createElementNS(kernel.svgsn, 'circle')
  // le cercle entourant l'icône
  svg.appendChild(circ)
  circ.setAttribute('cx', 16)
  circ.setAttribute('cy', 16)
  circ.setAttribute('r', 15)
  const filtre = (teinte === 'fonce') ? 'filtrebleu' : 'filtregris'
  circ.setAttribute('style', 'stroke:none;' + 'fill:url(#radial' + teinte + '); filter:url(#' + filtre + ');')
  const text = document.createElementNS(kernel.svgsn, 'text')
  text.setAttribute('pointer-events', 'none')
  text.appendChild(document.createTextNode('XML'))
  text.setAttribute('x', 16)
  text.setAttribute('y', 21)
  text.setAttribute('style', 'font-family:Arial;font-size:9pt;fill:white;text-anchor:middle')
  svg.appendChild(text)
  return svg
}

/**
 * Fonction rajoutant dans le svg parent des définitions defs qui serot utilisées
 * plusieurs fois dans les icônes (radialGradiant et marker pour la flèche de l'icône restart.
 * On les mets dans le svg barreIcones pour qu'il ne soient pas effacés lors de initialise()
 */
IepDoc.prototype.creeElementsPourIcones = function () {
  let defs = document.createElementNS(kernel.svgsn, 'defs')
  defs.appendChild(this.radialGradiant('#9999FF', '#000099', 'radialfonce'))
  this.svg.appendChild(defs)
  defs = document.createElementNS(kernel.svgsn, 'defs')
  defs.appendChild(this.radialGradiant('#CCCCCC', '#666666', 'radialclair'))
  this.svg.appendChild(defs)
  defs = document.createElementNS(kernel.svgsn, 'defs')
  defs.appendChild(this.radialGradiant('#FF9999', '#FF0000', 'radialrouge'))
  this.svg.appendChild(defs)
  defs = document.createElementNS(kernel.svgsn, 'defs')
  defs.appendChild(this.markerArrow('markerarrow'))
  this.barreIcones.appendChild(defs)
  defs = document.createElementNS(kernel.svgsn, 'defs')
  defs.appendChild(this.filtreEclairageIcone('filtrebleu'))
  this.barreIcones.appendChild(defs)
  defs = document.createElementNS(kernel.svgsn, 'defs')
  defs.appendChild(this.filtreEclairageIcone('filtregris'))
  this.barreIcones.appendChild(defs)
}
/**
 * Fonction renvoyant un radialGradiant utilisé pour le dessin des icônes
 * @param {string} clair : la couleur claire de gradiant
 * @param {string} fonce :  la couleur focée du gradiant
 * @param {Sring} id : l'id du gradiant
 * @returns {iep.radialGradiant.rg}
 */
IepDoc.prototype.radialGradiant = function (clair, fonce, id) {
  const rg = document.createElementNS(kernel.svgsn, 'radialGradient')
  rg.setAttribute('id', id)
  let stop = document.createElementNS(kernel.svgsn, 'stop')
  stop.setAttribute('offset', '0%')
  stop.setAttribute('style', 'stop-color:' + fonce + ';')
  rg.appendChild(stop)
  stop = document.createElementNS(kernel.svgsn, 'stop')
  stop.setAttribute('offset', '80%')
  stop.setAttribute('style', 'stop-color:' + clair + ';')
  rg.appendChild(stop)
  stop = document.createElementNS(kernel.svgsn, 'stop')
  stop.setAttribute('offset', '100%')
  stop.setAttribute('style', 'stop-color:' + fonce + ';')
  rg.appendChild(stop)
  return rg
}
/**
 * Fonction renvoyant un marker qui sera utilisé pour la flèche de l'icône restart
 * @param {string} id : L'id du marker
 * @returns {iep.markerArrow.marker}
 */
IepDoc.prototype.markerArrow = function (id) {
  const marker = document.createElementNS(kernel.svgsn, 'marker')
  marker.setAttribute('id', id)
  marker.setAttribute('markerWidth', '4')
  marker.setAttribute('markerHeight', '4')
  marker.setAttribute('refX', '0')
  marker.setAttribute('refY', '2')
  marker.setAttribute('orient', 'auto')
  const pathm = document.createElementNS(kernel.svgsn, 'path')
  pathm.setAttribute('d', 'M 0 0 2 2 0 4 Z')
  pathm.setAttribute('style', 'fill:white;')
  marker.appendChild(pathm)
  return marker
}
/**
 * Foncion renvoyant un filtre qui sera utilisé pour donner un aspect de relief aux icônes
 * @param {type} id : L'id du filtre
 * @returns {iep.filtreEclairageIcone.filter}
 */
IepDoc.prototype.filtreEclairageIcone = function (id) {
  const filter = document.createElementNS(kernel.svgsn, 'filter')
  filter.setAttribute('x', '0')
  filter.setAttribute('y', '0')
  filter.setAttribute('width', '100%')
  filter.setAttribute('height', '100%')
  filter.setAttribute('color-interpolation-filters', 'sRGB')
  const dl = document.createElementNS(kernel.svgsn, 'feDiffuseLighting')
  dl.setAttribute('lighting-color', 'white')
  dl.setAttribute('surfaceScale', '1')
  dl.setAttribute('diffuseConstant', '1.2')
  dl.setAttribute('in', 'SourceGraphic')
  dl.setAttribute('result', 'diffOut')
  const pointLight = document.createElementNS(kernel.svgsn, 'fePointLight')
  pointLight.setAttribute('x', '5')
  pointLight.setAttribute('y', '5')
  pointLight.setAttribute('z', '15')
  dl.appendChild(pointLight)
  filter.appendChild(dl)
  const fc = document.createElementNS(kernel.svgsn, 'feComposite')
  fc.setAttribute('in', 'SourceGraphic')
  fc.setAttribute('in2', 'diffOut')
  fc.setAttribute('operator', 'arithmetic')
  fc.setAttribute('k1', '1')
  fc.setAttribute('k2', '0')
  fc.setAttribute('k3', '0')
  fc.setAttribute('k4', '0')
  filter.appendChild(fc)
  filter.setAttribute('id', id)
  return filter
}
/**
 * Fonction renvoyant une fenêtre contenant le code XML de la figure
 */
IepDoc.prototype.popup = function () {
  // ouvre une fenetre sans barre d'etat, ni d'ascenceur
  const w = window.open('', 'popup', 'width=700,height=450,toolbar=no,scrollbars=no,resizable=yes')
  w.iepDoc = this
  const doc = w.document
  doc.title = 'Code Xml de la figure'
  const div = document.createElement('div')
  const form = document.createElement('form')
  div.appendChild(form)
  div.setAttribute('align', 'center')
  doc.body.appendChild(div)
  const ta = document.createElement('textarea')
  form.appendChild(ta)
  ta.setAttribute('cols', 80)
  ta.setAttribute('rows', 25)
  ta.setAttribute('readonly', true)
  ta.style.fontSize = '13px'
  ta.scrollIntoView()
  // Si le script ne comporte pas de retours à la ligne (une seule ligne), on en rajoute
  let ch = this.codexml
  ch = ch.replace(/>\s*</g, '>\n<')
  ta.appendChild(document.createTextNode(ch))
  ta.select()
  // w.onbeforeunload = function(){this.iepDoc.windowxml = null;};
  w.addEventListener('unload', function () { this.iepDoc.windowxml = null })
  return w
}
